'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { PDFDocument, degrees } from 'pdf-lib';
import Icon from '@/components/ui/AppIcon';
import { TOOL_COLORS } from '@/constants/toolColors';
import Script from 'next/script';
import { useToast } from '@/components/ui/Toast';
import { isEncryptedPDF } from '@/utils/pdf';
import UploadZone from '@/components/pdf/UploadZone';
import PDFViewerModal from '@/components/pdf/PDFViewerModal';
import { ensurePdfWorker, getPdfjsLib, renderPageThumbnail } from '@/utils/pdfThumbnailUtils';
import { FileCard } from '@/components/pdf/FileCard';
import { getPdfMeta, useEncryptedPdfToast } from '@/constants/pdfConfig';

const colors = TOOL_COLORS.organize;

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type?: string;
  file: File;
  pageCount: number;
}

interface PageThumbnail {
  id: string;
  originalIndex: number; // Index in the original PDF
  url: string;
  rotation: number; // user-applied rotation: 0 | 90 | 180 | 270
  intrinsicRotation: number; // PDF-embedded /Rotate value
  deleted: boolean;
  width: number;
  height: number;
}

const MAX_SIZE = 100 * 1024 * 1024; // 100MB

const ORGANIZE_THEME = {
  primary: '#8B5CF6',
  primaryLight: '#F5F3FF',
  primaryBorder: '#DDD6FE',
  primaryShadow: 'rgba(139, 92, 246, 0.28)',
};

export default function OrganizePDFUploader() {
  const [file, setFile] = useState<UploadedFile | null>(null);
  const [pages, setPages] = useState<PageThumbnail[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [processedPdfBlob, setProcessedPdfBlob] = useState<Blob | null>(null);
  // Cached pdf.js document — avoids reloading the file on every thumbnail re-render
  const pdfDocRef = useRef<any>(null);

  const { showToast } = useToast();
  const { showEncryptedPdfToast } = useEncryptedPdfToast();

  /** Handle File Upload */
  const handleFiles = useCallback(
    async (newFiles: FileList | null) => {
      if (!newFiles || newFiles.length === 0) return;

      const f = newFiles[0]; // Single file for organization tool

      if (
        !(f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')) ||
        f.size > MAX_SIZE
      ) {
        showToast('Invalid file type or file too large (max 100MB).', 'error');
        return;
      }

      try {
        // Pre-flight: check for encryption
        const alreadyEncrypted = await isEncryptedPDF(f);
        if (alreadyEncrypted) {
          showEncryptedPdfToast('organizing');
          return;
        }

        // Cleanup old thumbnails
        pages.forEach((p) => URL.revokeObjectURL(p.url));
        setPages([]);

        const meta = await getPdfMeta(f);
        setFile(meta);
        setIsDone(false);
      } catch (err) {
        console.error('Error reading PDF:', err);
        alert('Failed to read PDF file. It might be password protected.');
      }
    },
    [pages]
  );

  /** Lazy Thumbnail Generation */
  useEffect(() => {
    if (!file) return;

    let cancelled = false;
    const generatedUrls: string[] = [];

    const generateThumbnails = async () => {
      try {
        const bytes = await file.file.arrayBuffer();
        const pdfjsLib = getPdfjsLib();
        if (!pdfjsLib) {
          console.error('pdfjsLib not found');
          return;
        }

        ensurePdfWorker(pdfjsLib);

        const loadingTask = pdfjsLib.getDocument({ data: bytes });
        const pdf = await loadingTask.promise;
        pdfDocRef.current = pdf; // cache for thumbnail re-renders

        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) break;

          // No rotation param → pdf.js uses page's intrinsic /Rotate automatically
          const result = await renderPageThumbnail(pdf, i - 1);
          if (!result) continue;

          generatedUrls.push(result.url);

          const p: PageThumbnail = {
            id: crypto.randomUUID(),
            originalIndex: i - 1,
            url: result.url,
            rotation: 0,
            intrinsicRotation: result.intrinsicRotation,
            deleted: false,
            width: result.width,
            height: result.height,
          };

          // Append to existing state so mid-load rotations are not overwritten
          setPages((prev) => (cancelled ? prev : [...prev, p]));
          await new Promise((r) => setTimeout(r, 10));
        }
      } catch (err) {
        console.error('Thumbnail generation error:', err);
      }
    };

    generateThumbnails();

    return () => {
      cancelled = true;
      generatedUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [file]);

  // ── Thumbnail re-render helper ──────────────────────────────────────────────

  const reRenderSingleThumbnail = useCallback(async (pageIndex: number, totalRotation: number) => {
    const pdf = pdfDocRef.current;
    if (!pdf) return;
    const result = await renderPageThumbnail(pdf, pageIndex, { rotation: totalRotation });
    if (!result) return;
    setPages((prev) =>
      prev.map((p, i) => {
        if (i !== pageIndex) return p;
        URL.revokeObjectURL(p.url);
        return { ...p, url: result.url, width: result.width, height: result.height };
      })
    );
  }, []);

  /** Page Actions */
  const rotatePage = (index: number) => {
    setPages((prev) => {
      const page = prev[index];
      if (!page) return prev;
      const newUserRotation = (page.rotation + 90) % 360;
      const totalRotation = (page.intrinsicRotation + newUserRotation) % 360;
      setTimeout(() => reRenderSingleThumbnail(index, totalRotation), 0);
      return prev.map((p, i) => (i === index ? { ...p, rotation: newUserRotation } : p));
    });
  };

  const revertRotate = (index: number) => {
    setPages((prev) => {
      const page = prev[index];
      if (!page) return prev;
      setTimeout(() => reRenderSingleThumbnail(index, page.intrinsicRotation), 0);
      return prev.map((p, i) => (i === index ? { ...p, rotation: 0 } : p));
    });
  };

  const toggleDelete = (index: number) => {
    setPages((prev) => prev.map((p, i) => (i === index ? { ...p, deleted: !p.deleted } : p)));
  };

  const movePage = (index: number, direction: 'up' | 'down') => {
    setPages((prev) => {
      const arr = [...prev];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= arr.length) return arr;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      setDraggedIdx(null);
      return arr;
    });
  };

  /** Drag & Drop Reordering */
  const onDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIdx(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e: React.DragEvent, index: number) => {
    if (draggedIdx === null || draggedIdx === index) return;
    e.preventDefault();
  };

  const onDropPage = (e: React.DragEvent, index: number) => {
    if (draggedIdx === null || draggedIdx === index) return;
    e.preventDefault();
    setPages((prev) => {
      const arr = [...prev];
      const item = arr.splice(draggedIdx, 1)[0];
      arr.splice(index, 0, item);
      return arr;
    });
    setDraggedIdx(null);
  };

  /** Final PDF Processing */
  const handleOrganize = async () => {
    if (!file || pages.length === 0) return;

    const pagesToKeep = pages.filter((p) => !p.deleted);
    if (pagesToKeep.length === 0) {
      alert('You must keep at least one page.');
      return;
    }

    try {
      setIsProcessing(true);
      setProgress(0);

      const arrayBuffer = await file.file.arrayBuffer();
      const sourcePdf = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      const targetPdf = await PDFDocument.create();

      for (let i = 0; i < pagesToKeep.length; i++) {
        const p = pagesToKeep[i];
        const [copiedPage] = await targetPdf.copyPages(sourcePdf, [p.originalIndex]);

        // Apply rotation
        if (p.rotation !== 0) {
          const currentRotation = copiedPage.getRotation().angle;
          copiedPage.setRotation(degrees(currentRotation + p.rotation));
        }

        targetPdf.addPage(copiedPage);
        setProgress(Math.round(((i + 1) / pagesToKeep.length) * 100));
      }

      const pdfBytes = await targetPdf.save();
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });

      setProcessedPdfBlob(blob);
      setIsDone(true);
    } catch (err) {
      console.error('PDF processing error:', err);
      alert('Failed to process PDF.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = useCallback(() => {
    if (!processedPdfBlob) return;

    const url = URL.createObjectURL(processedPdfBlob);

    const a = document.createElement('a');

    a.href = url;
    a.download = `organized_${file?.name}`;

    document.body.appendChild(a);

    a.click();

    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 100);
  }, [processedPdfBlob, file]);

  const handleReset = () => {
    setFile(null);
    setPages([]);
    setIsDone(false);
    setProgress(0);
    setProcessedPdfBlob(null);
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-4">
      <Script
        src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.min.mjs"
        type="module"
        strategy="afterInteractive"
      />

      {file && (
        <PDFViewerModal
          file={file}
          isOpen={viewerOpen}
          currentPage={currentPage}
          onClose={() => setViewerOpen(false)}
          onPageChange={setCurrentPage}
          pageRotation={pages[currentPage]?.rotation ?? 0}
          onDelete={(i) => toggleDelete(i)}
          onRestore={(i) => toggleDelete(i)}
          isPageDeleted={(i) => pages[i]?.deleted ?? false}
          onRotate={(i) => rotatePage(i)}
          onRevertRotate={(i) => revertRotate(i)}
          isPageRotated={(i) => (pages[i]?.rotation ?? 0) !== 0}
        />
      )}

      {/* Upload Zone */}
      {!file && (
        <UploadZone
          onFilesSelected={handleFiles}
          multiple={false}
          accentColor={colors.primary}
          iconName="Squares2X2Icon"
          title="Drop PDF file here"
          subtitle="or click to browse — single file for organization"
          buttonText="Select PDF File"
          dragTitle="Drop your PDF here!"
        />
      )}

      {/* Page List View */}
      {file && !isDone && (
        <div className="flex flex-col lg:flex-row gap-6 mt-4">
          {/* ================= LEFT SIDE ================= */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <FileCard file={file} onRemove={handleReset} theme={ORGANIZE_THEME} />

            {/* Thumbnails Grid */}
            <div className="max-h-[70vh] overflow-y-auto pr-2 pb-20 lg:pb-0">
              <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {pages.length === 0
                  ? Array.from({ length: file.pageCount }).map((_, i) => (
                      <div
                        key={i}
                        className="aspect-square rounded-xl bg-slate-50 animate-pulse border border-slate-100"
                      />
                    ))
                  : pages.map((page, i) => {
                      return (
                        <div
                          key={page.id}
                          draggable
                          onDragStart={(e) => onDragStart(e, i)}
                          onDragOver={(e) => onDragOver(e, i)}
                          onDrop={(e) => onDropPage(e, i)}
                          className={`relative border rounded-xl overflow-hidden aspect-[100/140] group cursor-pointer transition hover:shadow-md hover:-translate-y-[1px] ${
                            draggedIdx === i ? 'opacity-30 scale-95' : 'opacity-100 scale-100'
                          } shadow-[0_2px_8px_rgba(0,0,0,0.04)]`}
                          style={{
                            borderColor: page.deleted
                              ? 'red'
                              : page.rotation !== 0
                                ? colors.primary
                                : '#EEEEF5',
                            borderWidth: '2px',
                          }}
                          onClick={() => {
                            setCurrentPage(i);
                            setViewerOpen(true);
                          }}
                        >
                          {/* Thumbnail Image Container */}
                          <img
                            src={page.url}
                            alt={`Page ${i + 1}`}
                            className="w-full h-full object-contain"
                          />

                          {/* Hover Eye Overlay */}
                          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center z-10 pointer-events-none">
                            <Icon
                              name="EyeIcon"
                              size={22}
                              className="text-white"
                              aria-hidden="true"
                            />
                          </div>

                          <div className="absolute top-2 left-2 z-20 opacity-0 group-hover:opacity-100 transition">
                            <div className="w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center text-slate-500 cursor-grab active:cursor-grabbing">
                              <Icon name="Bars3Icon" size={14} />
                            </div>
                          </div>

                          {/* Action Overlay */}
                          <div className="absolute top-2 right-2 flex flex-col gap-1.5 z-20 opacity-100">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                rotatePage(i);
                              }}
                              className="w-6 h-6 rounded-full shadow-md flex items-center justify-center text-white hover:scale-110 transition-transform"
                              title="Rotate 90°"
                              style={{ background: colors.primary }}
                            >
                              <Icon name="ArrowPathIcon" size={14} />
                            </button>
                            {page.rotation !== 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  revertRotate(i);
                                }}
                                className="w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center text-slate-500 hover:scale-110 transition-transform border border-slate-100"
                                title="Revert Rotate"
                              >
                                <Icon name="ArrowUturnLeftIcon" size={12} />
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDelete(i);
                              }}
                              className={`w-6 h-6 rounded-full shadow-md flex items-center justify-center transition-transform hover:scale-110 ${
                                page.deleted ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                              }`}
                              title={page.deleted ? 'Restore Page' : 'Remove Page'}
                            >
                              <Icon name={page.deleted ? 'PlusIcon' : 'XMarkIcon'} size={14} />
                            </button>
                          </div>

                          {/* Deleted Overlay */}
                          {page.deleted && (
                            <div className="absolute inset-0 bg-red-50/20 pointer-events-none flex items-center justify-center">
                              <span className="text-[10px] font-extrabold text-red-500 uppercase tracking-wider bg-white px-2 py-1 rounded-md shadow-sm border border-red-100">
                                Deleted
                              </span>
                            </div>
                          )}

                          <div className="absolute bottom-1 left-1 right-1 px-2 py-[2px] text-[10px] flex items-center z-20">
                            {/* Left: Current position */}
                            <div className="w-1/3 text-left">
                              <span
                                className="font-semibold text-slate-700"
                                title={`Current position: ${i + 1}`}
                              >
                                #{i + 1}
                              </span>
                            </div>

                            {/* Center: Original page */}
                            <div className="w-1/3 text-center">
                              <span
                                className="text-slate-500"
                                title={`Original page: ${page.originalIndex + 1}`}
                              >
                                {page.originalIndex + 1}
                              </span>
                            </div>

                            {/* Right: Rotation (only if changed) */}
                            <div className="w-1/3 text-right">
                              {(() => {
                                const deg = (page.intrinsicRotation + page.rotation) % 360;

                                return deg !== 0 ? (
                                  <span
                                    className="font-semibold"
                                    style={{ color: ORGANIZE_THEME.primary }}
                                    title={`Rotated: ${deg}°`}
                                  >
                                    {deg}°
                                  </span>
                                ) : null;
                              })()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
              </div>
            </div>
          </div>

          {/* ================= RIGHT SIDEBAR ================= */}
          <div className="w-full lg:w-[280px]">
            <div className="lg:sticky lg:top-6 space-y-4">
              <div className="p-3 rounded-xl border border-brand-border bg-brand-surface">
                <div className="grid grid-cols-3 gap-y-2 text-[11px] text-brand-muted">
                  <span className="flex items-center gap-1">
                    <Icon name="Bars3Icon" size={13} />
                    Drag
                  </span>

                  <span className="flex items-center gap-1">
                    <Icon name="ArrowPathIcon" size={13} />
                    Rotate
                  </span>

                  <span className="flex items-center gap-1">
                    <Icon name="ArrowUturnLeftIcon" size={13} />
                    Reset
                  </span>

                  <span className="flex items-center gap-1">
                    <Icon name="XMarkIcon" size={13} />
                    Remove
                  </span>

                  <span className="flex items-center gap-1">
                    <Icon name="PlusIcon" size={13} />
                    Restore
                  </span>

                  <span className="flex items-center gap-1">
                    <Icon name="EyeIcon" size={13} />
                    Preview
                  </span>
                </div>
              </div>

              {/* Organize Button */}
              <button
                onClick={handleReset}
                className="w-full py-3 rounded-xl border border-brand-border text-brand-mid font-semibold"
              >
                Reset All
              </button>

              {!isProcessing && (
                <button
                  onClick={handleOrganize}
                  className="w-full py-4 rounded-2xl font-heading font-bold text-white"
                  style={{
                    background: colors.primary,
                    boxShadow: colors.shadow,
                  }}
                >
                  Organize PDF
                </button>
              )}

              {/* Progress Bar */}
              {isProcessing && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">Processing PDF...</span>
                    <span className="text-sm font-bold" style={{ color: colors.primary }}>
                      {progress}%
                    </span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full transition-all duration-300"
                      style={{ width: `${progress}%`, background: colors.primary }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Done State */}
      {isDone && (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 pulse-anim bg-green-50">
            <Icon name="CheckBadgeIcon" size={42} variant="solid" className="text-green-600" />
          </div>

          <h3 className="font-heading font-bold mb-2 text-[20px] text-brand-dark">
            Organization Complete!
          </h3>
          <p className="text-brand-mid text-[14px] font-body mb-6">
            Your PDF has been reordered, rotated, and cleaned up exactly as you requested.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button
              className="sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary text-white border-none font-heading font-bold text-[15px] cursor-pointer shadow-[0_6px_20px_rgba(232,68,90,0.28)]"
              onClick={handleDownload}
              style={{
                backgroundColor: ORGANIZE_THEME.primary,
                boxShadow: '0 6px 20px ' + ORGANIZE_THEME.primaryShadow,
              }}
            >
              <Icon name="ArrowDownTrayIcon" size={18} variant="solid" />
              Download Organized PDF
            </button>
            <button
              onClick={handleReset}
              className="px-8 py-4 rounded-full border-2 border-brand-border font-heading font-bold text-brand-mid hover:bg-brand-surface transition-all active:scale-95 flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2"
            >
              <Icon name="ArrowPathIcon" size={18} />
              Organize Another PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
